import React, { useCallback, useEffect, useMemo } from 'react';
import type { FontId } from '@bynder-studio/structured-text';
import { Button, Dropdown, Form } from '@bynder/design-system';
import useFonts from 'packages/pages/editor/RightSideMenu/Shots/Text/Fonts/useFonts';
import generateTestId from 'packages/helpers/testIdHelpers';
import useTextSelection from 'packages/hooks/useTextSelection';
import { fontStyleName } from 'packages/pages/brandFonts/components/styleName';
import type { Props } from './types';
import type { ExtendedFont } from '../types';

const FontStyle = ({ creativeModel, selectedElement, disabled }: Props) => {
    const { id, locked } = selectedElement;
    const { fontId } = useTextSelection();
    const { fontsList, currentFont, isMixed, mixedValue } = useFonts();

    const family = fontsList[currentFont?.familyName];
    const fonts = useMemo(() => family?.fonts ?? [], [family]);

    const selectedFamilyNames = useMemo(() => {
        const names = new Set<string>();

        Object.values(fontsList).forEach(({ name, fonts }) => {
            if (fonts.some((f) => f.referenceIds.some((referenceId) => fontId.includes(String(referenceId))))) {
                names.add(name);
            }
        });

        return names;
    }, [fontId, fontsList]);

    const matchFontById = useCallback(
        (idToFind: FontId) =>
            fonts.find((f) => f.referenceIds.some((referenceId) => String(referenceId) === String(idToFind))),
        [fonts],
    );
    const fontStyleIds = new Set((fontId || []).map(String));

    const handleChange = useCallback<(font: ExtendedFont) => void>(
        (font) => {
            const param = {
                updateTextSettingForSelection: {
                    settings: {
                        fontId: font.id.toString(),
                    },
                },
            };

            creativeModel.updateElement(id, param);
        },
        [creativeModel, id],
    );

    // Rewrites the saved design: only fire when the run's id answers to no face in the shown family.
    useEffect(() => {
        const runFontId = fontId[0];

        if (!fonts.length || runFontId === undefined || matchFontById(runFontId)) {
            return;
        }

        handleChange(fonts[0]);
    }, [fonts, fontId, matchFontById, handleChange]);

    const isDisabled =
        selectedFamilyNames.size > 1 || disabled || locked || fonts.length === 1 || !currentFont?.isActive;

    return (
        <Form.Group>
            <Dropdown
                {...generateTestId('shots_text_style')}
                trigger={({ isOpen, ...triggerProps }) => (
                    <Button
                        aria-label="shots_text_style"
                        isPressed={isOpen}
                        isDisabled={isDisabled}
                        rightIcon={<Dropdown.Arrow />}
                        isFullWidth
                        {...triggerProps}
                    >
                        <Dropdown.SelectButtonText>
                            {!isMixed ? currentFont?.name : mixedValue}
                        </Dropdown.SelectButtonText>
                    </Button>
                )}
                position="bottom"
            >
                {family &&
                    fonts.map((font) => (
                        <Dropdown.Item
                            key={font.id}
                            isChecked={font.referenceIds.some((referenceId) => fontStyleIds.has(String(referenceId)))}
                            onClick={() => handleChange(font)}
                        >
                            {fontStyleName(family, font)}
                        </Dropdown.Item>
                    ))}
            </Dropdown>
        </Form.Group>
    );
};

export default FontStyle;
